import React, { FC, useCallback, useMemo } from 'react';
import { Box, Card, CardContent } from '@mui/material';
import { ChatConversaProps, Message } from './types';
import { chatStyles } from './styles';
import { useChatScroll } from './hooks/useChatScroll';
import { ChatHeader } from './components/ChatHeader';
import { ChatInput } from './components/ChatInput';
import ChatMessage from './ChatMessage';
import { NPSFeedback } from './components/NPSFeedback';
import { useNPSFeedback } from './hooks/useNPSFeedback';
import TypingAnimation from './TypingAnimation';
import ChatAudioMessage from './ChatAudioMessage';

const DEFAULT_HEIGHT = { xs: 400, sm: 450, md: 480 };

const formatTime = (): string => {
  return new Date().toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const createMessage = (text: string, type: 'text' | 'audio'): Message => ({
  from: 'user',
  text: text.trim(),
  time: formatTime(),
  type,
});

const getCardStyles = (customHeight?: any) => ({
  ...chatStyles.card,
  height: customHeight || DEFAULT_HEIGHT,
});

export const ChatConversa: FC<ChatConversaProps> = React.memo(
  ({
    loading,
    setLoading,
    showControls = false,
    onMinimize,
    onClose,
    onSendMessage,
    style,
    customStyles,
    messages,
    input,
    setInput,
    conversationId,
    onResolveConversationId,
  }) => {
    const {
      messagesContainerRef,
      scrollToBottom,
      handleScroll,
      resetUserScrolled,
    } = useChatScroll();

    const {
      showNPS,
      handleFeedback,
      handleDismiss,
      handleUserActivity,
      markUserMessageSent,
    } = useNPSFeedback(conversationId, scrollToBottom);

    const sendMessage = useCallback(async () => {
      if (!input.trim()) return;
      scrollToBottom(true);
      handleUserActivity();
      markUserMessageSent(input.trim());

      const message = createMessage(input, 'text');
      setInput('');
      resetUserScrolled();

      try {
        await onSendMessage(message);
      } catch (error) {
        console.error('Erro ao enviar mensagem:', error);
        setInput(typeof message.text === 'string' ? message.text : '');
      }
    }, [
      input,
      setInput,
      resetUserScrolled,
      onSendMessage,
      scrollToBottom,
      handleUserActivity,
      markUserMessageSent,
    ]);

    const handleAudioTranscription = useCallback(
      (text: string) => {
        const message = createMessage(text, 'audio');
        scrollToBottom(true);
        handleUserActivity();
        markUserMessageSent(text.trim());

        if (onSendMessage) {
          onSendMessage(message);
        }
      },
      [scrollToBottom, onSendMessage, handleUserActivity, markUserMessageSent]
    );

    const handleInputChange = useCallback(
      (value: string) => {
        setInput(value);
        handleUserActivity();
      },
      [setInput, handleUserActivity]
    );

    const messagesList = useMemo(() => {
      return messages?.map((msg, index) => {
        const isLastLiaMessage =
          msg.from === 'lia' &&
          messages.slice(index + 1).every((m) => m.from !== 'lia');

        if (msg.type === 'audio' && msg.from === 'lia') {
          return (
            <ChatAudioMessage
              key={index}
              msg={msg}
              onDone={scrollToBottom}
            />
          );
        }

        return (
          <ChatMessage
            key={index}
            msg={msg}
            isLastLiaMessage={isLastLiaMessage}
            onDone={scrollToBottom}
            onWordTyped={scrollToBottom}
          />
        );
      });
    }, [messages, scrollToBottom]);

    return (
      <Box sx={style}>
        <Card sx={getCardStyles(style?.height)}>
          <ChatHeader
            showControls={showControls}
            onMinimize={onMinimize}
            onClose={onClose}
            customStyles={customStyles}
            isExpanded={!!style?.height}
          />
          <CardContent
            ref={messagesContainerRef}
            onScroll={handleScroll}
            sx={chatStyles.messagesContainer}
          >
            <Box sx={{ flexGrow: 1 }} />
            {messagesList}
            {loading && <TypingAnimation />}
            {showNPS && (
              <NPSFeedback
                onFeedback={handleFeedback}
                onDismiss={handleDismiss}
                conversationId={conversationId}
              />
            )}
          </CardContent>
          <ChatInput
            input={input}
            setInput={handleInputChange}
            onSendMessage={sendMessage}
            onAudioTranscription={handleAudioTranscription}
            loading={loading}
            setLoading={setLoading}
            scrollToBottom={scrollToBottom}
            conversationId={conversationId}
            onResolveConversationId={onResolveConversationId}
          />
        </Card>
      </Box>
    );
  }
);

ChatConversa.displayName = 'ChatConversa';
